import type { AdminUser, Project } from '../../api/types';
import { Notice } from '../../components/ui/Notice';

/**
 * REMOVING SOMEONE FROM A PROJECT — the confirmation, as a STATE.
 *
 * 🔴 THIS REPLACES A `window.confirm` I SHOULD NEVER HAVE WRITTEN. `useCompleteSprint.ts` states the
 *   rule in this codebase's own words: *"CONFIRMATION IS A STATE, NOT A `window.confirm`"*. Mine was
 *   the only `window.confirm` in the entire app.
 *   ⭐ Same shape as the last two defects I shipped: the rule was already written down, one file
 *     away, and the note did not stop me. Only using the thing did — I hit it while testing on
 *     production and had to override `window.confirm` from the console to get past it.
 *
 * ⛔ FOUR THINGS A BROWSER CONFIRM CANNOT DO, and each is a real loss:
 *   1. Say the LAST-LEAD consequence in its own voice, visually separated from the question. In a
 *      confirm it is one run-on string with a `\n\n` in it.
 *   2. Carry the API's refusal if the removal fails — a confirm is gone by then, so the error had
 *      nowhere to appear next to what caused it.
 *   3. Render at 390px in this app's theme. A native confirm is neither styled nor dark-mode aware.
 *   4. Be driven by a test or by browser automation without stubbing a global.
 *
 * ⭐ THE WARNING IS SHOWN AND THE ACTION IS STILL OFFERED. A hard block would strand a project whose
 *   only lead has left the company — on the very screen that exists because people leave. What is
 *   refused is a SILENT removal, never the removal.
 */
export function RemoveMemberDialog({
  user,
  project,
  warning,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  user: AdminUser;
  project: Project;
  /** The last-lead sentence, or null. See `lastLeadWarning`. */
  warning: string | null;
  busy: boolean;
  /** The API's own words for a refusal — something WAS sent. */
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const title = `Remove ${user.display_name} from ${project.key}?`;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md space-y-3 rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">{title}</h2>

        <p className="text-sm text-[color:var(--color-ink-muted)]">
          They keep their PlanHub account and can still sign in. They will no longer be able to write
          to <strong>{project.name}</strong> — every attempt is refused with{' '}
          <em>only an active member of this project may…</em>
        </p>

        {/* ⭐ The consequence gets its own block, not a `\n\n` inside a sentence. This is the whole
            reason it is a dialog: an admin about to strand a project should read that as a warning,
            not as more of the question. */}
        {warning ? (
          <Notice tone="warning" title="This is the only lead">
            <p className="break-words">{warning}</p>
          </Notice>
        ) : null}

        {/* ⚠️ A confirm could not do this at all: by the time the API refuses, the confirm is gone
            and the message has nowhere to appear beside the thing that caused it. */}
        {error ? (
          <Notice tone="error" title="The API refused this removal">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-danger,#b91c1c)] px-4 text-sm font-semibold text-white disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Removing…' : `Remove from ${project.key}`}
          </button>
        </div>
      </div>
    </div>
  );
}
